// Display-only mirror of `server/mecharena/tools.py::TOOL_SPECS`.
//
// **Source of truth lives in Python.** When a balance change touches a
// stat, it lands there first and this mirror updates in the same PR.
// The proto regen (planned alongside the v2 OpenAPI cut) will
// eventually generate this file; until then it's hand-maintained and
// the test suite pins counts + invariants so drift is loud.
//
// Numeric IDs match `proto/mecharena/v1/game.proto::ToolId` and the
// existing display map at `packages/ui/src/lib/tools.ts`. Names match
// `TOOL_NAMES` exactly; the two are designed to co-exist (this module
// imports + re-exports them).

import { TOOL_NAMES } from "./tools.js";

/** Functional category — mirrors `mecharena.tools.ToolKind`. */
export enum ToolKind {
  UNSPECIFIED = 0,
  MOVEMENT = 1,
  DIRECT_FIRE = 2,
  MISSILE = 3,
  MELEE = 4,
  REACTIVE = 5,
  PASSIVE = 6,
  SENSOR = 7,
  HEAT_UTILITY = 8,
}

/** SQI bonus class — mirrors `mecharena.tools.LaserClass`. */
export enum LaserClass {
  NONE = 0,
  STANDARD = 1,
  PULSE = 2,
}

/** Where a tool physically mounts on the chassis — see `server/mecharena/mount.py`.
 *  TORSO mounts can be crit-destroyed by side-zone hits; ARM mounts by
 *  arm destruction; NONE means the tool lives on the mech as a whole
 *  and only goes away when the mech dies. */
export type MountClass = "ARM" | "TORSO" | "NONE";

/** Sentinel for `ammo` on tools with infinite charges (lasers, PPC, melee). */
export const AMMO_UNLIMITED = 0xffffffff;

/** Mirror of `server/mecharena/tools.py::ToolSpec`. Optional fields
 *  default to 0 / unlimited / no-prerequisite to match the Python
 *  dataclass defaults. */
export interface ToolSpec {
  id: number;
  name: string;
  kind: ToolKind;
  /** §6.7 power budget — every loadout has 24 power total. */
  powerCost: number;
  /** Where it mounts; derived from the ARM_MOUNTED / TORSO_MOUNTED
   *  frozensets in `server/mecharena/mount.py`. */
  mount: MountClass;

  maxMovement: number;
  baseDamage: number;
  heatCost: number;

  /** Inclusive — distance must be >= minRange for the shot to be legal.
   *  0 means no minimum (point-blank allowed). */
  minRange: number;
  rangeShortMax: number;
  rangeMediumMax: number;
  rangeLongMax: number;
  maxRange: number;

  /** `AMMO_UNLIMITED` for energy weapons; finite for ballistics / missiles. */
  ammo: number;
  stability: number;
  missileCount: number;
  missileDamage: number;
  /** Limited-use charges (POP_SMOKE / MASC / TAG / FLUSH_COOLANT). 0 = unlimited. */
  charges: number;
  laserClass: LaserClass;

  /** Heat removed when this tool fires (FLUSH_COOLANT). */
  removesHeat: number;
  prerequisites: readonly number[];

  /** One-line prose: what this tool IS. Empty for mechanical fillers. */
  description: string;
  /** Directional doctrine: when to reach for it. Empty for fillers. */
  usageHint: string;
}

// Curated ARM / TORSO mount sets per `server/mecharena/mount.py`.
const ARM_MOUNTED = new Set<number>([
  /* AC_2 */ 12, /* AC_5 */ 13, /* MEDIUM_LASER */ 7, /* SMALL_LASER */ 6,
  /* MACHINE_GUN */ 11, /* PULSE_MEDIUM */ 9,
]);
const TORSO_MOUNTED = new Set<number>([
  /* AC_10 */ 14, /* AC_20 */ 15, /* GAUSS */ 16, /* LARGE_LASER */ 8,
  /* PPC */ 10,
  /* LRM_5..20 */ 20, 21, 22, 23,
  /* SRM_2..6 */ 17, 18, 19,
]);

function mountOf(id: number): MountClass {
  if (ARM_MOUNTED.has(id)) return "ARM";
  if (TORSO_MOUNTED.has(id)) return "TORSO";
  return "NONE";
}

/** Helper used during catalog construction — fills in defaults. */
function spec(
  partial: Partial<ToolSpec> & Pick<ToolSpec, "id" | "kind" | "powerCost">,
): ToolSpec {
  const id = partial.id;
  const name = TOOL_NAMES.get(id);
  if (!name) {
    throw new Error(`tool-catalog: no name for id=${id}`);
  }
  return {
    id,
    name,
    kind: partial.kind,
    powerCost: partial.powerCost,
    mount: mountOf(id),
    maxMovement: partial.maxMovement ?? 0,
    baseDamage: partial.baseDamage ?? 0,
    heatCost: partial.heatCost ?? 0,
    minRange: partial.minRange ?? 0,
    rangeShortMax: partial.rangeShortMax ?? 0,
    rangeMediumMax: partial.rangeMediumMax ?? 0,
    rangeLongMax: partial.rangeLongMax ?? 0,
    maxRange: partial.maxRange ?? 0,
    ammo: partial.ammo ?? AMMO_UNLIMITED,
    stability: partial.stability ?? 0,
    missileCount: partial.missileCount ?? 0,
    missileDamage: partial.missileDamage ?? 0,
    charges: partial.charges ?? 0,
    laserClass: partial.laserClass ?? LaserClass.NONE,
    removesHeat: partial.removesHeat ?? 0,
    prerequisites: partial.prerequisites ?? [],
    description: partial.description ?? "",
    usageHint: partial.usageHint ?? "",
  };
}

// IDs — match `proto/mecharena/v1/game.proto::ToolId` and the existing
// TOOL_NAMES map. Local constants here so the catalog below reads
// without magic numbers.
const T = {
  WALK: 1, DASH: 2, JUMP: 3, MASC: 4, JUMP_JETS: 5,
  SMALL_LASER: 6, MEDIUM_LASER: 7, LARGE_LASER: 8, PULSE_MEDIUM: 9,
  PPC: 10, MACHINE_GUN: 11, AC_2: 12, AC_5: 13, AC_10: 14, AC_20: 15,
  GAUSS: 16,
  SRM_2: 17, SRM_4: 18, SRM_6: 19,
  LRM_5: 20, LRM_10: 21, LRM_15: 22, LRM_20: 23,
  PUNCH: 24,
  POP_SMOKE: 25, AMS: 26, ECM: 27, BAP: 28, TAG: 29, TARGETING_COMPUTER: 30,
  FLUSH_COOLANT: 31, DOUBLE_HEAT_SINK: 32, CASE: 33,
} as const;

/** The complete v2 catalog. Keyed by tool name (not id) so consumers
 *  reading from agent YAMLs (where tools are written by name) can do
 *  `TOOL_CATALOG[name]` without an id-lookup hop. */
export const TOOL_CATALOG: Readonly<Record<string, ToolSpec>> = Object.freeze({
  // §6.1 Movement
  WALK: spec({ id: T.WALK, kind: ToolKind.MOVEMENT, powerCost: 1, maxMovement: 3, heatCost: 1 }),
  DASH: spec({
    id: T.DASH, kind: ToolKind.MOVEMENT, powerCost: 2, maxMovement: 5, heatCost: 2,
    description: "Burst movement; locks facing this turn.",
    usageHint: "Close range or reset distance. Plan weapon facing before committing — DASH cannot rotate.",
  }),
  JUMP: spec({
    id: T.JUMP, kind: ToolKind.MOVEMENT, powerCost: 2, maxMovement: 3, heatCost: 3,
    prerequisites: [T.JUMP_JETS],
    description: "Jump-jet movement; ignores terrain MP, free facing on landing.",
    usageHint: "Reset positioning when an opponent crowds you. Effective range shrinks as heat MP penalties bite — burn it before you spike heat.",
  }),
  MASC: spec({
    id: T.MASC, kind: ToolKind.MOVEMENT, powerCost: 2, heatCost: 2, charges: 1, maxMovement: 10,
    description: "Once-per-match speed burst — doubles WALK/DASH max for the turn.",
    usageHint: "Save for the alpha turn. The next-turn stagger is a real cost; spend it for a kill, not a flank.",
  }),
  JUMP_JETS: spec({
    id: T.JUMP_JETS, kind: ToolKind.PASSIVE, powerCost: 2,
    description: "Passive prerequisite for JUMP.",
    usageHint: "Always paired with JUMP — pick both or neither.",
  }),

  // §6.2 Direct-fire
  SMALL_LASER: spec({
    id: T.SMALL_LASER, kind: ToolKind.DIRECT_FIRE, powerCost: 1,
    baseDamage: 3, heatCost: 1, rangeShortMax: 3, maxRange: 3,
    laserClass: LaserClass.STANDARD,
  }),
  MEDIUM_LASER: spec({
    id: T.MEDIUM_LASER, kind: ToolKind.DIRECT_FIRE, powerCost: 2,
    baseDamage: 5, heatCost: 3,
    rangeShortMax: 3, rangeMediumMax: 6, rangeLongMax: 9, maxRange: 9,
    laserClass: LaserClass.STANDARD,
    description: "Medium-band energy weapon. Reliable, unlimited ammo.",
    usageHint: "Energy fallback that doesn't run dry. Pair with one ballistic to keep a stable volley.",
  }),
  LARGE_LASER: spec({
    id: T.LARGE_LASER, kind: ToolKind.DIRECT_FIRE, powerCost: 4,
    baseDamage: 8, heatCost: 8,
    rangeShortMax: 5, rangeMediumMax: 10, rangeLongMax: 15, maxRange: 15,
    laserClass: LaserClass.STANDARD,
    description: "Heavy energy beam. High per-shot heat.",
    usageHint: "Sustained damage at MEDIUM+ range. Pair with DOUBLE_HEAT_SINK to keep heat in band.",
  }),
  PULSE_MEDIUM: spec({
    id: T.PULSE_MEDIUM, kind: ToolKind.DIRECT_FIRE, powerCost: 3,
    baseDamage: 6, heatCost: 4,
    rangeShortMax: 2, rangeMediumMax: 4, rangeLongMax: 6, maxRange: 6,
    laserClass: LaserClass.PULSE,
  }),
  PPC: spec({
    id: T.PPC, kind: ToolKind.DIRECT_FIRE, powerCost: 5,
    baseDamage: 10, heatCost: 10, minRange: 3,
    rangeShortMax: 6, rangeMediumMax: 12, rangeLongMax: 18, maxRange: 18,
    stability: 5,
    description: "Long-range knockout punch.",
    usageHint: "Cycle every other turn so DHS dissipates the spike. Skip below minRange — wasted shot.",
  }),
  MACHINE_GUN: spec({
    id: T.MACHINE_GUN, kind: ToolKind.DIRECT_FIRE, powerCost: 1,
    baseDamage: 2, heatCost: 0,
    rangeShortMax: 2, rangeMediumMax: 3, maxRange: 3,
  }),
  AC_2: spec({
    id: T.AC_2, kind: ToolKind.DIRECT_FIRE, powerCost: 2,
    baseDamage: 2, heatCost: 1,
    rangeShortMax: 8, rangeMediumMax: 16, rangeLongMax: 24, maxRange: 24,
    ammo: 20,
  }),
  AC_5: spec({
    id: T.AC_5, kind: ToolKind.DIRECT_FIRE, powerCost: 3,
    baseDamage: 5, heatCost: 1,
    rangeShortMax: 6, rangeMediumMax: 12, rangeLongMax: 18, maxRange: 18,
    ammo: 15,
  }),
  AC_10: spec({
    id: T.AC_10, kind: ToolKind.DIRECT_FIRE, powerCost: 4,
    baseDamage: 10, heatCost: 3,
    rangeShortMax: 5, rangeMediumMax: 10, rangeLongMax: 15, maxRange: 15,
    ammo: 10,
  }),
  AC_20: spec({
    id: T.AC_20, kind: ToolKind.DIRECT_FIRE, powerCost: 6,
    baseDamage: 20, heatCost: 7,
    rangeShortMax: 3, rangeMediumMax: 6, rangeLongMax: 9, maxRange: 9,
    ammo: 6, stability: 8,
  }),
  GAUSS: spec({
    id: T.GAUSS, kind: ToolKind.DIRECT_FIRE, powerCost: 6,
    baseDamage: 15, heatCost: 1, minRange: 2,
    rangeShortMax: 7, rangeMediumMax: 15, rangeLongMax: 22, maxRange: 22,
    ammo: 8, stability: 5,
    description: "Sustainable long-range mainstay; +1 heat per shot.",
    usageHint: "Lead with GAUSS. Eight shots total — pick targets carefully. Switch off when target leaves max range or magazine nears cookoff threshold.",
  }),

  // §6.3 Missile
  SRM_2: spec({
    id: T.SRM_2, kind: ToolKind.MISSILE, powerCost: 1, heatCost: 2,
    rangeShortMax: 3, rangeMediumMax: 6, rangeLongMax: 9, maxRange: 9,
    missileCount: 2, missileDamage: 2, stability: 2, ammo: 50,
  }),
  SRM_4: spec({
    id: T.SRM_4, kind: ToolKind.MISSILE, powerCost: 2, heatCost: 3,
    rangeShortMax: 3, rangeMediumMax: 6, rangeLongMax: 9, maxRange: 9,
    missileCount: 4, missileDamage: 2, stability: 4, ammo: 25,
  }),
  SRM_6: spec({
    id: T.SRM_6, kind: ToolKind.MISSILE, powerCost: 3, heatCost: 4,
    rangeShortMax: 3, rangeMediumMax: 6, rangeLongMax: 9, maxRange: 9,
    missileCount: 6, missileDamage: 2, stability: 6, ammo: 15,
  }),
  LRM_5: spec({
    id: T.LRM_5, kind: ToolKind.MISSILE, powerCost: 2, heatCost: 2, minRange: 4,
    rangeShortMax: 7, rangeMediumMax: 14, rangeLongMax: 21, maxRange: 21,
    missileCount: 5, missileDamage: 1, stability: 3, ammo: 24,
  }),
  LRM_10: spec({
    id: T.LRM_10, kind: ToolKind.MISSILE, powerCost: 3, heatCost: 4, minRange: 4,
    rangeShortMax: 7, rangeMediumMax: 14, rangeLongMax: 21, maxRange: 21,
    missileCount: 10, missileDamage: 1, stability: 5, ammo: 12,
  }),
  LRM_15: spec({
    id: T.LRM_15, kind: ToolKind.MISSILE, powerCost: 4, heatCost: 5, minRange: 4,
    rangeShortMax: 7, rangeMediumMax: 14, rangeLongMax: 21, maxRange: 21,
    missileCount: 15, missileDamage: 1, stability: 7, ammo: 8,
  }),
  LRM_20: spec({
    id: T.LRM_20, kind: ToolKind.MISSILE, powerCost: 5, heatCost: 6, minRange: 4,
    rangeShortMax: 7, rangeMediumMax: 14, rangeLongMax: 21, maxRange: 21,
    missileCount: 20, missileDamage: 1, stability: 10, ammo: 6,
  }),

  // §6.4 Melee
  PUNCH: spec({
    id: T.PUNCH, kind: ToolKind.MELEE, powerCost: 1,
    baseDamage: 8, heatCost: 1,
    rangeShortMax: 1, maxRange: 1, stability: 5,
  }),

  // §6.5 Defense + utility
  POP_SMOKE: spec({
    id: T.POP_SMOKE, kind: ToolKind.REACTIVE, powerCost: 2, heatCost: 0, charges: 3,
  }),
  AMS: spec({ id: T.AMS, kind: ToolKind.REACTIVE, powerCost: 3, heatCost: 1 }),
  ECM: spec({ id: T.ECM, kind: ToolKind.PASSIVE, powerCost: 3 }),
  BAP: spec({ id: T.BAP, kind: ToolKind.PASSIVE, powerCost: 2 }),
  TAG: spec({
    id: T.TAG, kind: ToolKind.SENSOR, powerCost: 2, heatCost: 1, charges: 5,
    rangeShortMax: 5, rangeMediumMax: 9, rangeLongMax: 15, maxRange: 15,
  }),
  TARGETING_COMPUTER: spec({
    id: T.TARGETING_COMPUTER, kind: ToolKind.PASSIVE, powerCost: 3,
  }),

  // §6.6 Heat utility
  FLUSH_COOLANT: spec({
    id: T.FLUSH_COOLANT, kind: ToolKind.HEAT_UTILITY, powerCost: 1, charges: 1, removesHeat: 8,
  }),
  DOUBLE_HEAT_SINK: spec({
    id: T.DOUBLE_HEAT_SINK, kind: ToolKind.HEAT_UTILITY, powerCost: 2,
  }),
  CASE: spec({ id: T.CASE, kind: ToolKind.HEAT_UTILITY, powerCost: 1 }),
});

/** All tool names, sorted by category then alphabetical. Stable order
 *  for picker grids + tests. */
export const TOOL_NAMES_ORDERED: readonly string[] = Object.keys(TOOL_CATALOG);

/** Look up a tool spec by name (case-sensitive) or numeric id. Returns
 *  `undefined` for unknown tools — callers decide whether to throw or
 *  render a placeholder. */
export function getToolSpec(toolOrId: string | number): ToolSpec | undefined {
  if (typeof toolOrId === "number") {
    return Object.values(TOOL_CATALOG).find((s) => s.id === toolOrId);
  }
  return TOOL_CATALOG[toolOrId];
}

/** Filter the catalog by kind. Stable insertion order. */
export function getToolsByKind(kind: ToolKind): ToolSpec[] {
  return Object.values(TOOL_CATALOG).filter((s) => s.kind === kind);
}

/** Human-readable label for a ToolKind. Used in chip badges + picker
 *  filters. */
export function toolKindLabel(kind: ToolKind): string {
  switch (kind) {
    case ToolKind.MOVEMENT: return "Movement";
    case ToolKind.DIRECT_FIRE: return "Direct-fire";
    case ToolKind.MISSILE: return "Missile";
    case ToolKind.MELEE: return "Melee";
    case ToolKind.REACTIVE: return "Reactive";
    case ToolKind.PASSIVE: return "Passive";
    case ToolKind.SENSOR: return "Sensor";
    case ToolKind.HEAT_UTILITY: return "Heat utility";
    case ToolKind.UNSPECIFIED: return "—";
  }
}

/** Returns whether the tool has finite ammo (i.e. a magazine). */
export function hasAmmo(spec: ToolSpec): boolean {
  return spec.ammo !== AMMO_UNLIMITED && spec.ammo > 0;
}
